import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, RefreshCw, Loader2 } from "lucide-react";

interface BrevoMapping {
  id: string;
  brevo_attribute: string;
  source_field: string;
  transform: string | null;
  is_active: boolean;
  display_order: number;
}

interface BrevoAttribute {
  name: string;
  category: string;
  type: string;
}

const PROFILE_FIELDS = [
  "id",
  "email",
  "first_name",
  "last_name",
  "phone",
  "member_type",
  "referral_code",
  "created_at",
  "joined_at",
  "bio",
  "avatar_url",
];

const TRANSFORMS = [
  { value: "none", label: "None" },
  { value: "sanitize_phone", label: "Sanitize Phone (E.164)" },
  { value: "fallback_created_at", label: "Fallback to created_at" },
];

export function BrevoMappingsManager() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState<BrevoMapping | null>(null);
  const [formData, setFormData] = useState({
    brevo_attribute: "",
    source_field: "",
    transform: "none",
  });

  const { data: mappings, isLoading } = useQuery({
    queryKey: ["brevo-mappings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("brevo_attribute_mappings")
        .select("*")
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data as BrevoMapping[];
    },
  });

  const { data: brevoAttributes, isLoading: isLoadingAttributes, refetch: refetchAttributes } = useQuery({
    queryKey: ["brevo-attributes"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("sync-user-to-brevo", {
        body: { action: "get_attributes" },
      });
      if (error) throw error;
      return (data?.attributes || []) as BrevoAttribute[];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  const createMutation = useMutation({
    mutationFn: async (data: Omit<BrevoMapping, "id" | "display_order">) => {
      const maxOrder = mappings?.reduce((max, m) => Math.max(max, m.display_order), 0) || 0;
      const { error } = await supabase.from("brevo_attribute_mappings").insert({
        ...data,
        display_order: maxOrder + 1,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brevo-mappings"] });
      toast.success("Mapping created");
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(`Failed to create: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<BrevoMapping> & { id: string }) => {
      const { error } = await supabase.from("brevo_attribute_mappings").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brevo-mappings"] });
      toast.success("Mapping updated");
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("brevo_attribute_mappings").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["brevo-mappings"] });
      toast.success("Mapping deleted");
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMapping(null);
    setFormData({ brevo_attribute: "", source_field: "", transform: "none" });
  };

  const handleOpenCreate = () => {
    setEditingMapping(null);
    setFormData({ brevo_attribute: "", source_field: "", transform: "none" });
    setDialogOpen(true);
  };

  const handleEdit = (mapping: BrevoMapping) => {
    setEditingMapping(mapping);
    setFormData({
      brevo_attribute: mapping.brevo_attribute,
      source_field: mapping.source_field,
      transform: mapping.transform || "none",
    });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.brevo_attribute || !formData.source_field) {
      toast.error("Brevo attribute and source field are required");
      return;
    }

    const data = {
      brevo_attribute: formData.brevo_attribute.toUpperCase(),
      source_field: formData.source_field,
      transform: formData.transform === "none" ? null : formData.transform,
      is_active: true,
    };

    if (editingMapping) {
      updateMutation.mutate({ id: editingMapping.id, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  const toggleActive = (mapping: BrevoMapping) => {
    updateMutation.mutate({ id: mapping.id, is_active: !mapping.is_active });
  };

  // Combine fetched Brevo attributes with any custom ones already in mappings
  const allBrevoAttributes = [
    ...(brevoAttributes?.map((a) => a.name) || []),
    ...(mappings?.map((m) => m.brevo_attribute) || []),
  ].filter((v, i, a) => a.indexOf(v) === i); // unique

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg">Brevo Attribute Mappings</CardTitle>
          <CardDescription>Configure how profile fields map to Brevo contact attributes</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchAttributes()}
            disabled={isLoadingAttributes}
          >
            {isLoadingAttributes ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
          <Button size="sm" onClick={handleOpenCreate}>
            <Plus className="h-4 w-4 mr-1" />
            Add Mapping
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Dialog open={dialogOpen} onOpenChange={(open) => !open && handleCloseDialog()}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingMapping ? "Edit Mapping" : "Add Mapping"}</DialogTitle>
              <DialogDescription>Map a profile field to a Brevo contact attribute</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Brevo Attribute</Label>
                {brevoAttributes && brevoAttributes.length > 0 ? (
                  <Select
                    value={formData.brevo_attribute}
                    onValueChange={(value) => setFormData({ ...formData, brevo_attribute: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a Brevo attribute" />
                    </SelectTrigger>
                    <SelectContent>
                      {allBrevoAttributes.map((attr) => (
                        <SelectItem key={attr} value={attr}>
                          {attr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder="e.g. FIRSTNAME"
                    value={formData.brevo_attribute}
                    onChange={(e) => setFormData({ ...formData, brevo_attribute: e.target.value })}
                    className="uppercase"
                  />
                )}
              </div>
              <div className="space-y-2">
                <Label>Source Field</Label>
                <Select
                  value={formData.source_field}
                  onValueChange={(value) => setFormData({ ...formData, source_field: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a profile field" />
                  </SelectTrigger>
                  <SelectContent>
                    {PROFILE_FIELDS.map((field) => (
                      <SelectItem key={field} value={field}>
                        {field}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Transform (optional)</Label>
                <Select
                  value={formData.transform}
                  onValueChange={(value) => setFormData({ ...formData, transform: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No transform" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSFORMS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
                {editingMapping ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Brevo Attribute</TableHead>
                <TableHead>Source Field</TableHead>
                <TableHead>Transform</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappings?.map((mapping) => (
                <TableRow key={mapping.id} className="h-9">
                  <TableCell className="font-mono text-sm py-1">{mapping.brevo_attribute}</TableCell>
                  <TableCell className="font-mono text-sm py-1">{mapping.source_field}</TableCell>
                  <TableCell className="text-sm py-1 text-muted-foreground">
                    {TRANSFORMS.find((t) => t.value === (mapping.transform || "none"))?.label || "—"}
                  </TableCell>
                  <TableCell className="py-1">
                    <Switch checked={mapping.is_active} onCheckedChange={() => toggleActive(mapping)} />
                  </TableCell>
                  <TableCell className="py-1">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(mapping)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => deleteMutation.mutate(mapping.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
